import { useEffect, useState } from 'react';
import type { LifecycleFeed } from '../types/lifecycle';

export type LifecycleFeedSource = 'production' | 'fixture';

export type LifecycleFeedState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'schema_mismatch'; expected: number; found: unknown }
  | { status: 'ok'; feed: LifecycleFeed; source: LifecycleFeedSource };

const EXPECTED_SCHEMA_VERSION = 1;

const LIFECYCLE_PRIMARY  = './data/proposal-lifecycle.published.json';
const LIFECYCLE_FALLBACK = './data/proposal-lifecycle.published.sample.json';

export function useLifecycleFeed(
  primaryUrl: string = LIFECYCLE_PRIMARY,
  fallbackUrl: string = LIFECYCLE_FALLBACK,
): LifecycleFeedState {
  const [state, setState] = useState<LifecycleFeedState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const sources: Array<{ url: string; source: LifecycleFeedSource }> = [
        { url: primaryUrl, source: 'production' },
        { url: fallbackUrl, source: 'fixture' },
      ];
      let lastError = '';
      for (const { url, source } of sources) {
        try {
          const r = await fetch(url);
          if (!r.ok) {
            lastError = `HTTP ${r.status} ${r.statusText} (${url})`;
            continue;
          }
          const text = await r.text();
          if (text.trimStart().startsWith('<')) {
            lastError = `Expected JSON, got HTML (${url})`;
            continue;
          }
          const feed = JSON.parse(text) as LifecycleFeed;
          if (!feed || typeof feed !== 'object' || feed.meta?.schema_version !== EXPECTED_SCHEMA_VERSION) {
            if (!cancelled) {
              setState({
                status: 'schema_mismatch',
                expected: EXPECTED_SCHEMA_VERSION,
                found: feed?.meta?.schema_version ?? 'missing',
              });
            }
            return;
          }
          if (!cancelled) setState({ status: 'ok', feed, source });
          return;
        } catch (err) {
          lastError = String(err);
        }
      }
      if (!cancelled) setState({ status: 'error', message: lastError || 'Unknown error' });
    }

    load();
    return () => { cancelled = true; };
  }, [primaryUrl, fallbackUrl]);

  return state;
}
